/**
 * Series Selector Component
 * Multi-select dropdown for choosing chart series to display
 */

import { LitElement, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { SeriesName } from '../types/pricing.js';

/**
 * Event detail for series change
 */
export interface SeriesChangeEvent {
  selectedSeries: SeriesName[];
}

@customElement('series-selector')
export class SeriesSelector extends LitElement {
  // Use Light DOM for consistency with landing page
  protected createRenderRoot() {
    return this;
  }

  @property({ type: Array })
  selectedSeries: SeriesName[] = [];

  @property({ type: Array })
  availableSeries: SeriesName[] = [];

  @state()
  private isOpen = false;

  /**
   * Toggle dropdown open/close
   */
  private toggleDropdown() {
    this.isOpen = !this.isOpen;
  }

  /**
   * Handle series checkbox toggle
   */
  private handleSeriesToggle(series: SeriesName) {
    const isSelected = this.selectedSeries.includes(series);

    if (isSelected) {
      // Prevent deselecting the last series
      if (this.selectedSeries.length === 1) {
        return;
      }
      this.selectedSeries = this.selectedSeries.filter((s) => s !== series);
    } else {
      this.selectedSeries = [...this.selectedSeries, series];
    }

    // Emit custom event
    this.dispatchEvent(
      new CustomEvent<SeriesChangeEvent>('series-change', {
        detail: { selectedSeries: this.selectedSeries },
        bubbles: true,
        composed: true,
      })
    );
  }

  /**
   * Close dropdown when clicking outside
   */
  private handleClickOutside = (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    if (!this.contains(target)) {
      this.isOpen = false;
    }
  };

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('click', this.handleClickOutside);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.handleClickOutside);
  }

  render() {
    return html`
      <div class="relative">
        <!-- Dropdown Button -->
        <button
          @click="${this.toggleDropdown}"
          class="px-4 py-2 bg-surface-elevated text-text-primary text-sm border border-surface-border hover:bg-surface-overlay transition-colors duration-200 flex items-center gap-2"
        >
          <span>
            ${this.selectedSeries.length === 1
              ? this.selectedSeries[0]
              : `${this.selectedSeries.length} series selected`}
          </span>
          <svg
            class="w-4 h-4 transition-transform ${this.isOpen
              ? 'rotate-180'
              : ''}"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>

        <!-- Dropdown Menu -->
        ${this.isOpen
          ? html`
              <div
                class="absolute z-10 mt-2 w-64 bg-surface-elevated border border-surface-border shadow-lg"
              >
                <div class="py-2">
                  ${this.availableSeries.map(
                    (series) => html`
                      <label
                        class="flex items-center px-4 py-2 hover:bg-surface-overlay cursor-pointer transition-colors duration-200"
                      >
                        <input
                          type="checkbox"
                          .checked="${this.selectedSeries.includes(series)}"
                          @change="${() => this.handleSeriesToggle(series)}"
                          class="w-4 h-4 border-surface-border bg-transparent checked:bg-brand-primary focus:ring-brand-primary cursor-pointer"
                        />
                        <span class="ml-3 text-sm text-text-primary">
                          ${series}
                        </span>
                      </label>
                    `
                  )}
                </div>
                <div
                  class="px-4 py-2 text-xs text-text-tertiary border-t border-surface-border"
                >
                  ${this.selectedSeries.length} of ${this.availableSeries.length}
                  selected
                </div>
              </div>
            `
          : ''}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'series-selector': SeriesSelector;
  }
}
